import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ExternalLink, Loader2, Mail, MoreHorizontal, Pencil } from "lucide-react";
import { toast } from "sonner";
import { formatPaymentFulfillmentStatus } from "@/lib/paymentFulfillmentDisplay";

interface PurchasedProductLine {
  productId?: string;
  variantId?: string;
  quantity?: number;
  description?: string;
}

interface PagePayment {
  _id: string;
  lead?: string;
  customerEmail?: string;
  amount: number;
  currency?: string;
  paymentIntentId?: string;
  createdAt: string;
  purchasedProducts?: PurchasedProductLine[];
  fulfillmentStatus?: string | null;
}

interface OrdersTabProps {
  pageId?: string;
}

function formatDate(dateString: string) {
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function formatAmount(amount: number, currency?: string) {
  const currencySymbol = currency === "usd" ? "$" : "";
  return `${currencySymbol}${(amount / 100).toFixed(2)}`;
}

function getPurchaseDisplayName(line: PurchasedProductLine) {
  const desc = line.description?.trim();
  if (desc) return desc;
  return "Unknown product";
}

export function OrdersTab({ pageId }: OrdersTabProps) {
  const [payments, setPayments] = useState<PagePayment[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSendingShipmentEmails, setIsSendingShipmentEmails] = useState(false);

  const fetchPayments = async () => {
    if (!pageId) return;

    setIsLoading(true);
    try {
      const response = await fetch(`/api/pages/${pageId}/payments`);
      if (response.ok) {
        const data = await response.json();
        setPayments((data.payments || []) as PagePayment[]);
      }
    } catch (error) {
      console.error("Failed to fetch page orders:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    void fetchPayments();
  }, [pageId]);

  const notShippedCount = useMemo(
    () => payments.filter((p) => p.fulfillmentStatus !== "shipped").length,
    [payments],
  );

  const handleSendAllShipmentNotifications = async () => {
    if (!pageId || isSendingShipmentEmails) return;

    if (notShippedCount === 0) {
      toast.error("All orders on this page are already marked as shipped");
      return;
    }

    if (
      !window.confirm(
        `Send shipment notification emails for ${notShippedCount} order${notShippedCount === 1 ? "" : "s"}? Orders already marked shipped will be skipped.`,
      )
    ) {
      return;
    }

    setIsSendingShipmentEmails(true);
    try {
      const response = await fetch(`/api/pages/${pageId}/send-shipment-emails`, {
        method: "POST",
      });
      const data = (await response.json().catch(() => ({}))) as {
        error?: string;
        sent?: number;
        skipped?: number;
        failed?: { paymentId: string; error: string }[];
      };

      if (!response.ok) {
        toast.error(typeof data.error === "string" ? data.error : "Could not send shipment emails");
        return;
      }

      const sent = data.sent ?? 0;
      const skipped = data.skipped ?? 0;
      const failed = data.failed ?? [];

      if (failed.length > 0) {
        toast.warning(
          `Sent ${sent}, skipped ${skipped} already shipped, ${failed.length} failed`,
        );
      } else if (sent === 0) {
        toast.info(skipped > 0 ? "All orders were already marked as shipped" : "No shipment emails sent");
      } else {
        toast.success(
          `Shipment emails sent for ${sent} order${sent === 1 ? "" : "s"}${skipped > 0 ? ` (${skipped} skipped)` : ""}`,
        );
      }

      await fetchPayments();
    } catch {
      toast.error("Could not send shipment emails");
    } finally {
      setIsSendingShipmentEmails(false);
    }
  };

  const totalRevenue = useMemo(
    () => payments.reduce((sum, payment) => sum + (payment.amount || 0), 0),
    [payments],
  );

  if (!pageId) {
    return (
      <div className="flex-1 overflow-auto p-4">
        <p className="text-sm text-gray-500">No page selected.</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex-1 flex justify-center items-center min-h-[40vh]">
        <Loader2 className="animate-spin h-12 w-12 text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-auto p-4">
      {payments.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          No orders found for this page.
        </div>
      ) : (
        <div className="space-y-4">
          <div className="flex items-start justify-end">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="icon"
                  className="shrink-0"
                  aria-label="Orders actions"
                  disabled={isSendingShipmentEmails}
                >
                  {isSendingShipmentEmails ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <MoreHorizontal className="h-4 w-4" />
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  disabled={isSendingShipmentEmails || notShippedCount === 0}
                  onClick={() => void handleSendAllShipmentNotifications()}
                >
                  <Mail className="h-4 w-4" />
                  Send Shipment Notifications
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-xl">
            <Card>
              <CardContent className="pt-6">
                <div className="text-center">
                  <p className="text-2xl font-semibold">
                    ${(totalRevenue / 100).toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </p>
                  <p className="text-sm text-muted-foreground mt-1">Revenue</p>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="text-center">
                  <p className="text-2xl font-semibold">{payments.length}</p>
                  <p className="text-sm text-muted-foreground mt-1">Orders</p>
                </div>
              </CardContent>
            </Card>
          </div>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Email</TableHead>
                  <TableHead>Purchases</TableHead>
                  <TableHead>Fulfillment</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead className="w-[52px]" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {payments.map((payment) => (
                  <TableRow key={payment._id}>
                    <TableCell>
                      {payment.customerEmail ? (
                        payment.lead ? (
                          <Link
                            to={`/contacts/${String(payment.lead)}/edit`}
                            className="text-primary underline underline-offset-4"
                          >
                            {payment.customerEmail}
                          </Link>
                        ) : (
                          payment.customerEmail
                        )
                      ) : (
                        "—"
                      )}
                    </TableCell>
                    <TableCell className="max-w-[280px] text-sm whitespace-normal">
                      {!payment.purchasedProducts?.length ? (
                        "—"
                      ) : (
                        <div className="flex flex-col gap-0.5">
                          {payment.purchasedProducts.map((line, idx) => {
                            const qty =
                              line.quantity != null && line.quantity > 1
                                ? ` ×${line.quantity}`
                                : "";
                            return (
                              <div key={`${payment._id}-${idx}`}>
                                {getPurchaseDisplayName(line)}
                                {qty ? (
                                  <span className="text-muted-foreground">{qty}</span>
                                ) : null}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-sm whitespace-nowrap">
                      {formatPaymentFulfillmentStatus(payment.fulfillmentStatus)}
                    </TableCell>
                    <TableCell>
                      {formatAmount(payment.amount, payment.currency)}
                    </TableCell>
                    <TableCell>{formatDate(payment.createdAt)}</TableCell>
                    <TableCell className="p-2" onClick={(e) => e.stopPropagation()}>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            aria-label="Order actions"
                          >
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem asChild>
                            <Link to={`/sales/orders/${payment._id}`}>
                              <Pencil className="h-4 w-4" />
                              Edit
                            </Link>
                          </DropdownMenuItem>
                          {payment.paymentIntentId ? (
                            <DropdownMenuItem asChild>
                              <a
                                href={`https://dashboard.stripe.com/payments/${payment.paymentIntentId}`}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                <ExternalLink className="h-4 w-4" />
                                Stripe
                              </a>
                            </DropdownMenuItem>
                          ) : null}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      )}
    </div>
  );
}
